import React, { useEffect, useState, Fragment, useCallback } from "react";
import {
  Button,
  Col,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Modal,
  message,
  Spin,
  DatePicker,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import HeadingComponent from "../../components/HeadingComponent";
import { Coupon, Coupon_Form } from "../../utils/constants";
import CustomTable from "../../components/CustomTable";
import dayjs from "dayjs";
import { getAllPrices } from "../PackagePrice/APIs/priceApi";
import {
  addCoupon,
  deleteCoupon,
  getAllCoupons,
  updateCoupon,
} from "./APIs/couponApi";

const { confirm } = Modal;

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);

  const columns = [
    {
      title: "Code",
      dataIndex: "coupon_code",
    },

    {
      title: "Discount Type",
      dataIndex: "discount_type",
    },

    {
      title: "Discount",
      dataIndex: "discount",
    },

    {
      title: "Limit",
      dataIndex: "usage_per_coupon",
    },

    {
      title: "Per user",
      dataIndex: "usage_per_user",
    },

    {
      title: "Status",
      dataIndex: "status",
    },

    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <EditOutlined
            className="icons_style"
            onClick={() => handleEdit(record)}
          />
          <DeleteOutlined
            className="icons_style"
            onClick={() => showDeleteConfirm(record)}
          />
        </Space>
      ),
    },
  ];
  const showDeleteConfirm = ({ id }) => {
    confirm({
      title: `Are you sure delete this package?`,
      icon: <DeleteOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        handleDelete(id);
      },
    });
  };
  const handleEdit = (record) => {
    setEditCoupon(record);
    setIsEdit(true);
  };
  const handleDelete = async (id) => {
    setLoading(true);
    await deleteCoupon(id).finally(() => {
      setLoading(false);
    });
    const filtered = coupons?.filter((x) => x.id !== id);
    setCoupons(filtered);
    message.success("Coupon deleted!");
  };

  const getCoupons = async () => {
    try {
      setLoading(true);
      let response = await getAllCoupons();
      setCoupons(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getPrices = async () => {
    try {
      setLoading(true);
      let response = await getAllPrices();
      setPrices(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getPrices();
    getCoupons();
  }, []);

  const setCouponsCallback = useCallback((data) => {
    setCoupons((prevState) => {
      if (prevState.find((row) => row.id === data.id)) {
        const index = prevState.findIndex((row) => row.id === data.id);
        prevState.splice(index, 1, data);
      } else {
        prevState.push(data);
      }
      return [...prevState];
    });
  }, []);

  return (
    <div>
      <div className="justify_between">
        <HeadingComponent text="Coupons" />
        <AddEditForm
          setCoupons={setCouponsCallback}
          isEdit={isEdit}
          editCoupon={editCoupon}
          setIsEdit={setIsEdit}
          prices={prices}
        />
      </div>
      <br />

      <CustomTable dataSource={coupons} columns={columns} loading={loading} />
    </div>
  );
};

export default Coupons;

const AddEditForm = ({
  isEdit = false,
  setCoupons,
  prices,
  editCoupon,
  setIsEdit,
}) => {
  const [open, setOpen] = useState(isEdit);
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (isEdit && editCoupon) {
      let safeValues = {
        ...editCoupon,
        expired_at: editCoupon?.expired_at
          ? dayjs(editCoupon?.expired_at)
          : null,
      };
      form.setFieldsValue(safeValues);
      setOpen(true);
    }
  }, [isEdit, editCoupon, form]);

  const showDrawer = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    form.resetFields();
  };

  const onSubmit = () => {
    form.validateFields().then((data) => {
      isEdit ? handleUpdate(data) : handleAdd(data);
    });
  };

  const handleAdd = async (data) => {
    const obj = {
      ...data,
      expired_at: data.expired_at ? data.expired_at.toISOString() : null, // Ensure proper formatting
    };
    setFormLoading(true);
    const response = await addCoupon(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newCoupon = { ...obj, id: response };
      setCoupons(newCoupon);
      message.success("Package added");
      handleClose();
    }
  };

  const handleUpdate = async (data) => {
    const obj = {
      ...data,
      expired_at: data.expired_at ? data.expired_at.toISOString() : null,
      id: editCoupon?.id,
    };
    setFormLoading(true);
    const response = await updateCoupon(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newCoupon = { ...obj, id: response };
      setCoupons(newCoupon);
      message.success("Coupon Updated");
      handleClose();
    }
  };

  return (
    <Fragment>
      <Button
        type="primary"
        onClick={showDrawer}
        icon={<PlusOutlined />}
        size="medium"
      >
        Add Coupon
      </Button>

      <Drawer
        title={isEdit ? `Edit ${Coupon}` : `Add ${Coupon}`}
        width={720}
        onClose={handleClose}
        open={open}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <Spin spinning={formLoading}>
          <Form layout="vertical" form={form} onFinish={onSubmit}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.coupon_code.name}
                  label={Coupon_Form.coupon_code.label}
                  rules={Coupon_Form.coupon_code.rules}
                >
                  <Input placeholder={Coupon_Form.coupon_code.placeholder} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.status.name}
                  label={Coupon_Form.status.label}
                  rules={Coupon_Form.status.rules}
                >
                  <Select
                    options={Coupon_Form.status.options}
                    placeholder={Coupon_Form.status.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={Coupon_Form.description.name}
                  label={Coupon_Form.description.label}
                >
                  <Input.TextArea
                    defaultValue=""
                    maxLength={20}
                    placeholder={Coupon_Form.description.placeholder}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.stripe_promo_id.name}
                  label={Coupon_Form.stripe_promo_id.label}
                  rules={Coupon_Form.stripe_promo_id.rules}
                >
                  <Input
                    placeholder={Coupon_Form.stripe_promo_id.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.discount_type.name}
                  label={Coupon_Form.discount_type.label}
                  rules={Coupon_Form.discount_type.rules}
                >
                  <Select
                    options={Coupon_Form.discount_type.options}
                    placeholder={Coupon_Form.discount_type.placeholder}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.discount.name}
                  label={Coupon_Form.discount.label}
                  rules={Coupon_Form.discount.rules}
                >
                  <InputNumber
                    className="full_width"
                    min={1}
                    placeholder={Coupon_Form.discount.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.expired_at.name}
                  label={Coupon_Form.expired_at.label}
                  rules={Coupon_Form.expired_at.rules}
                >
                  <DatePicker
                    minDate={dayjs()}
                    className="full_width"
                    min={1}
                    placeholder={Coupon_Form.expired_at.placeholder}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.price_id.name}
                  label={Coupon_Form.price_id.label}
                  rules={Coupon_Form.price_id.rules}
                >
                  <Select
                    maxTagCount="responsive"
                    mode="multiple"
                    options={prices?.map((item) => ({
                      label: item.package_price_name,
                      value: item.stripe_price_id,
                    }))}
                    placeholder={Coupon_Form.price_id.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.usage_per_coupon.name}
                  label={Coupon_Form.usage_per_coupon.label}
                  rules={Coupon_Form.usage_per_coupon.rules}
                >
                  <InputNumber
                    className="full_width"
                    min={1}
                    placeholder={Coupon_Form.usage_per_coupon.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Coupon_Form.usage_per_user.name}
                  label={Coupon_Form.usage_per_user.label}
                  rules={Coupon_Form.usage_per_user.rules}
                >
                  <InputNumber
                    className="full_width"
                    min={1}
                    placeholder={Coupon_Form.usage_per_user.placeholder}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Flex justify="flex-end">
              <Space>
                <Button onClick={handleClose}>Cancel</Button>
                <Button htmlType="submit" type="primary">
                  Submit
                </Button>
              </Space>
            </Flex>
          </Form>
        </Spin>
      </Drawer>
    </Fragment>
  );
};
